import React from "react";
import { Check, BookMarked, ExternalLink } from "lucide-react";
import useReveal from "../hooks/useReveal";
import useDocMeta from "../hooks/useDocMeta";
import ContactSection from "../components/ContactSection";
import FAQSection from "../components/FAQSection";
import HeroCover from "../components/HeroCover";
import { ACCELERATED_TMS_PAGE as A, IMAGES, META, FAQ_SECTIONS } from "../mock";

const ACC_BULLETS = [
  "Full 6-week course delivered in just 5 days",
  "Fast symptom relief — often within days",
  "Proven safety profile",
  "Clinically validated for treatment-resistant depression",
  "Minimal disruption to work, travel & daily life",
];

export default function AcceleratedTMS() {
  useReveal();
  useDocMeta(META.acceleratedTms || META.tms);
  return (
    <div>
      <HeroCover
        title="Accelerated TMS: Rapid-Acting Therapy for Depression"
        subtitle="A high-density form of TMS that delivers short, concentrated sessions throughout the day, so you can complete a full course of treatment in far less time."
        bullets={ACC_BULLETS}
        badges={[]}
        image={IMAGES.heroClinic}
        defaultHelp="Accelerated TMS"
        reviewName="Josh"
        showRibbon={true}
      />

      {/* Intro — media block (text + image) */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-brand-500 font-semibold tracking-[0.2em] text-xs uppercase">{A.eyebrow}</span>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-4">{A.title}</h2>
            <p className="text-brand-900/75 leading-relaxed">{A.intro}</p>
          </div>
          <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[4/3]">
            <img src={IMAGES.wellnessStudio} alt="Accelerated TMS treatment at AwareHub" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* 5-Day Protocol */}
      <section className="py-12 sm:py-16 bg-brand-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-3">{A.protocolTitle}</h2>
          <p className="text-brand-900/75 mb-6">{A.protocolIntro}</p>
          <div className="grid md:grid-cols-3 gap-6">
            {A.protocol.map((p, i) => (
              <div key={i} className="rounded-2xl bg-white border border-brand-100 p-6 shadow-sm">
                <h3 className="font-display text-xl text-brand-800">{p.h}</h3>
                <p className="mt-2 text-brand-900/70 leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-brand-900/70 italic leading-relaxed">{A.itbsNote}</p>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-6">{A.advantagesTitle}</h2>
          <div className="grid md:grid-cols-2 gap-5 items-stretch">
            {A.advantages.map((a, i) => (
              <div
                key={i}
                className={`flex items-start gap-4 rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 border border-brand-200 p-6 h-full ${
                  i === A.advantages.length - 1 && A.advantages.length % 2 === 1 ? "md:col-span-2" : ""
                }`}
              >
                <Check className="h-5 w-5 mt-1 shrink-0 text-brand-600" strokeWidth={3} />
                <div><h3 className="font-semibold text-brand-800">{a.h}</h3><p className="mt-1 text-brand-900/70">{a.b}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal candidate — image + text */}
      <section className="py-12 sm:py-16 bg-brand-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[4/3]">
            <img src={IMAGES.loveseat} alt="Consultation at AwareHub" className="h-full w-full object-cover" />
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-3">{A.candidateTitle}</h2>
            <p className="text-brand-900/75 mb-5">{A.candidateIntro}</p>
            <ul className="space-y-3">
              {A.candidate.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-brand-900/80"><Check className="h-5 w-5 mt-0.5 shrink-0 text-brand-500" strokeWidth={3} />{c}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Mapping technology + tailored */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-2">{A.mappingTitle}</h2>
            <p className="text-brand-600 font-semibold mb-4">{A.mappingSubtitle}</p>
            <div className="space-y-4">
              {A.mappingBody.map((p, i) => <p key={i} className="text-brand-900/75 leading-relaxed">{p}</p>)}
            </div>
            <p className="mt-4 text-brand-900/60 text-sm italic">{A.mappingNote}</p>
            <div className="mt-8">
              <h3 className="font-display text-xl text-brand-800 mb-2">{A.tailoredTitle}</h3>
              <p className="text-brand-900/75 leading-relaxed">{A.tailoredBody}</p>
            </div>
          </div>
          <div className="order-1 lg:order-2 rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[4/3]">
            <img src={IMAGES.tealChairs} alt="TMS mapping technology at AwareHub" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Scientific research & references */}
      <section className="py-12 sm:py-16 bg-sand-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal rounded-[2rem] bg-brand-900 text-white p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <BookMarked className="h-5 w-5 text-brand-200" />
            </span>
            <h2 className="font-display text-xl sm:text-2xl">{A.referencesTitle}</h2>
          </div>
          <ol className="space-y-4 list-decimal list-inside">
            {A.references.map((r, i) => (
              <li key={i} className="text-white/80 leading-relaxed text-sm">
                {r.text}
                {r.links && (
                  <span className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 pl-1">
                    {r.links.map((l, j) => (
                      <a
                        key={j}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-200 hover:text-white underline underline-offset-2 transition-colors"
                        data-testid={`reference-link-${i}-${j}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />{l.label}
                      </a>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <FAQSection background="bg-brand-50" sections={[FAQ_SECTIONS.acceleratedTms]} pageTitle="Accelerated TMS — Frequently Asked Questions" />

      <ContactSection />
    </div>
  );
}
